// hooks/auth/useAuth2FAVerify.ts
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from '@tanstack/react-router';

import { useAppDispatch } from '@/store';
import {
  selectAuthStatus,
  selectAuthError,
  selectFieldError,
  selectTwoFactorMethod,
  selectSessionToken,
  selectTwoFactorMaskedDestination,
  selectTwoFactorToken,
} from '@/store/selectors/auth';
import { verify2faCodeThunk } from '@/store/thunks/auth/verify2faCodeThunk';
import { resend2faCodeThunk } from '@/store/thunks/auth/resend2faCodeThunk';
import { clearLoginError } from '@/store/slices/auth/authSlice';

/**
 * Hook for /login/2fa/verify route
 * Handles 2FA code verification, code resend, and navigation to app
 *
 * Prerequisites:
 * - User must have sessionToken from credential verification
 * - User must have selected twoFactorMethod
 * - Route guard ensures these requirements
 *
 * @returns {Object} Hook state and handlers
 * @returns {Object} return.state - Current verification state
 * @returns {boolean} return.state.isLoading - Loading indicator during code verification
 * @returns {string} [return.state.error] - Global error message if verification fails
 * @returns {Object} return.state.fieldErrors - Field-level validation errors (code)
 * @returns {TwoFactorMethod} return.state.twoFactorMethod - Selected 2FA method (sms | email)
 * @returns {Object} return.handlers - Action handlers
 * @returns {Function} return.handlers.verify - Verify 2FA code
 * @returns {Function} return.handlers.resend - Resend verification code
 * @returns {Function} return.handlers.back - Navigate back to method selection
 *
 * @example
 * const { state, handlers } = useAuth2FAVerify();
 *
 * return (
 *   <AuthCode
 *     method={state.twoFactorMethod}
 *     onBack={handlers.back}
 *     onResend={handlers.resend}
 *     onSubmit={handlers.verify}
 *     loading={state.isLoading}
 *     error={state.error}
 *   />
 * );
 */
export function useAuth2FAVerify() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const authStatus = useSelector(selectAuthStatus);
  const error = useSelector(selectAuthError);
  const fieldErrors = useSelector(selectFieldError);
  const twoFactorMethod = useSelector(selectTwoFactorMethod);
  const twoFactorToken = useSelector(selectTwoFactorToken);
  const masked = useSelector(selectTwoFactorMaskedDestination);
  const isLoading = authStatus === 'loading';

  useEffect(() => {
    dispatch(clearLoginError());
  }, [dispatch]);

  // TODO Move to route guard
  useEffect(() => {
    if (twoFactorToken === undefined || twoFactorMethod === undefined) {
      void navigate({ to: '/login' });
    }
  }, [twoFactorToken, twoFactorMethod, navigate]);

  const verify = async(code: string) => {
    // Guard - should never happen due to route guard
    if (twoFactorToken === undefined) {
      await navigate({ to: '/login' });
      return;
    }

    const action = await dispatch(verify2faCodeThunk({ code }));

    if (verify2faCodeThunk.fulfilled.match(action)) {
      await navigate({ to: '/app' });
    }
  };

  const resend = async() => {
    if (twoFactorToken === undefined) {
      await navigate({ to: '/login' });
      return;
    }
    // Fire and forget - success/error shown via Redux state
    await dispatch(resend2faCodeThunk({method: twoFactorMethod ?? 'email'}));
  };

  const back = async() => {
    await navigate({ to: '/login/2fa/select' });
  };

  return {
    state: {
      isLoading,
      error,
      fieldErrors,
      twoFactorMethod,
      masked
    },
    handlers: { verify, resend, back },
  };
}
